import { Form, Formik, useFormikContext } from 'formik';
import { useMemo, useRef, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@uirouter/react';
import { array, boolean, number, object, SchemaOf, string } from 'yup';

import { useCurrentUser } from '@/react/hooks/useUser';
import { EdgeGroupsSelector } from '@/react/edge/edge-stacks/components/EdgeGroupsSelector';
import { EdgeStack, StaggerConfig } from '@/react/edge/edge-stacks/types';
import { EdgeGroup } from '@/react/edge/edge-groups/types';
import { notifySuccess } from '@/portainer/services/notifications';
import {
  ChartVersion,
  getSearchHelmRepo,
} from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmRepoVersions';
import { HelmVersionSelect } from '@/react/kubernetes/helm/components/HelmVersionSelect';
import { HelmValuesInput } from '@/react/kubernetes/helm/components/HelmValuesInput';
import { HelmTemplatesSelect } from '@/react/kubernetes/helm/components/HelmTemplatesSelect';
import { useHelmChartValues } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmChartValues';
import { useHelmChartList } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmChartList';
import {
  useUserHelmRepositories,
  flattenHelmRegistries,
} from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmRegistries';
import { Chart } from '@/react/kubernetes/helm/types';
import {
  Option,
  PortainerSelect,
} from '@/react/components/form-components/PortainerSelect';
import { RegistryValue } from '@/react/kubernetes/helm/components/HelmRegistrySelect';

import { FormSection } from '@@/form-components/FormSection';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { LoadingButton } from '@@/buttons';

import {
  StaggerFieldset,
  staggerConfigValidation,
} from '../../components/StaggerFieldset';
import { getDefaultStaggerConfig } from '../../components/StaggerFieldset.types';
import { HelmInstallOptionsSection } from '../../CreateView/HelmChartForm/HelmInstallOptionsSection';

import { useUpdateEdgeStackHelmRepoMutation } from './useUpdateEdgeStackHelmRepoMutation';
import { useStaggerUpdateStatus } from './useStaggerUpdateStatus';

interface FormValues {
  edgeGroups: Array<EdgeGroup['Id']>;
  helmRepoUrl: string;
  helmChartName: string;
  helmChartVersion: string;
  helmValuesInline: string;
  helmAtomic: boolean;
  helmTimeout: string;
  helmNamespace: string;
  helmReleaseName: string;
  staggerConfig: StaggerConfig;
}

function getLabelForVersion(
  version: string,
  index: number,
  currentVersion: string
): string {
  if (index === 0 && version !== currentVersion) {
    return `${version} (latest)`;
  }
  if (version === currentVersion) {
    return `${version} (current)`;
  }
  return version;
}

export function HelmRepositoryEditForm({
  edgeStack,
}: {
  edgeStack: EdgeStack;
}) {
  const mutation = useUpdateEdgeStackHelmRepoMutation();
  const router = useRouter();

  const initialValues: FormValues = {
    edgeGroups: edgeStack.EdgeGroups,
    helmRepoUrl: edgeStack.HelmConfig?.ChartURL ?? '',
    helmChartName: edgeStack.HelmConfig?.ChartName ?? '',
    helmChartVersion: edgeStack.HelmConfig?.ChartVersion ?? '',
    helmValuesInline: edgeStack.HelmConfig?.ValuesInline ?? '',
    helmAtomic: edgeStack.HelmConfig?.Atomic ?? false,
    helmTimeout: edgeStack.HelmConfig?.Timeout ?? '',
    helmNamespace: edgeStack.HelmConfig?.Namespace ?? 'default',
    helmReleaseName: `edge-${edgeStack.Name}`,
    staggerConfig: edgeStack.StaggerConfig ?? getDefaultStaggerConfig(),
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={formValidation()}
    >
      <InnerForm edgeStack={edgeStack} isLoading={mutation.isLoading} />
    </Formik>
  );

  function handleSubmit(values: FormValues) {
    mutation.mutate(
      {
        id: edgeStack.Id,
        edgeGroups: values.edgeGroups,
        helmConfig: {
          ChartPath: '',
          ChartURL: values.helmRepoUrl,
          ChartName: values.helmChartName,
          ChartVersion: values.helmChartVersion,
          ValuesInline: values.helmValuesInline,
          Atomic: values.helmAtomic,
          Timeout: values.helmTimeout,
          Namespace: values.helmNamespace,
        },
        staggerConfig: values.staggerConfig,
      },
      {
        onSuccess: () => {
          notifySuccess('Success', 'Stack successfully updated');
          router.stateService.go('^');
        },
      }
    );
  }
}

function InnerForm({
  edgeStack,
  isLoading,
}: {
  edgeStack: EdgeStack;
  isLoading: boolean;
}) {
  const { user } = useCurrentUser();
  const { values, setFieldValue, setValues, isValid, errors } =
    useFormikContext<FormValues>();

  const staggerUpdateStatus = useStaggerUpdateStatus(edgeStack.Id);
  const staggerUpdating =
    staggerUpdateStatus.data === 'updating' &&
    values.staggerConfig.StaggerOption === 2; // Parallel

  // Chart source and chart selection state
  const [selectedRepoUrl, setSelectedRepoUrl] = useState<string | null>(
    edgeStack.HelmConfig?.ChartURL ?? null
  );
  const [selectedChart, setSelectedChart] = useState<Chart | null>(null);

  // Build flat list of user repos for source dropdown
  const repoOptionsQuery = useUserHelmRepositories({
    select: (registries) =>
      flattenHelmRegistries(registries).map<Option<string>>((url) => ({
        label: url,
        value: url,
      })),
  });

  // Fetch chart list for the selected repo
  const chartListQuery = useHelmChartList(
    user.Id,
    0,
    { url: selectedRepoUrl ?? undefined },
    false // isOCI = false
  );

  const selectedRegistryValue: RegistryValue | null = selectedRepoUrl
    ? { registryUrl: selectedRepoUrl, isOCI: false, name: selectedRepoUrl }
    : null;

  // Fetch versions for the selected chart/repo
  const versionOptionsQuery = useQuery<
    ChartVersion[],
    unknown,
    Option<ChartVersion>[]
  >({
    queryKey: ['helm', 'versions', values.helmChartName, values.helmRepoUrl],
    queryFn: () =>
      getSearchHelmRepo({
        chart: values.helmChartName,
        repo: values.helmRepoUrl,
      }),
    enabled: !!(values.helmChartName && values.helmRepoUrl),
    staleTime: 60 * 60 * 1000,
    select: (versions) =>
      versions.map((v, index) => ({
        label: getLabelForVersion(
          v.version,
          index,
          edgeStack.HelmConfig?.ChartVersion ?? ''
        ),
        value: v,
      })),
  });

  // Pre-select the stored chart once the chart list loads
  const didAutoSelectChartRef = useRef(false);
  useEffect(() => {
    if (
      !didAutoSelectChartRef.current &&
      chartListQuery.data?.length &&
      edgeStack.HelmConfig?.ChartName
    ) {
      const matchingChart = chartListQuery.data.find(
        (c) => c.name === edgeStack.HelmConfig?.ChartName
      );
      if (matchingChart) {
        setSelectedChart(matchingChart);
        didAutoSelectChartRef.current = true;
      }
    }
  }, [chartListQuery.data, edgeStack.HelmConfig?.ChartName]);

  // Pre-select the stored version on initial load
  const didAutoSelectRef = useRef(false);
  useEffect(() => {
    const currentLength = versionOptionsQuery.data?.length ?? 0;
    if (!didAutoSelectRef.current && currentLength > 0) {
      const storedVersion = edgeStack.HelmConfig?.ChartVersion;
      const matchingVersion = versionOptionsQuery.data?.find(
        (v) => v.value?.version === storedVersion
      );
      if (matchingVersion) {
        setFieldValue('helmChartVersion', matchingVersion.value.version);
      } else if (!values.helmChartVersion) {
        const latest = versionOptionsQuery.data?.[0]?.value?.version;
        if (latest) {
          setFieldValue('helmChartVersion', latest);
        }
      }
      didAutoSelectRef.current = true;
    }
  }, [
    versionOptionsQuery.data,
    values.helmChartVersion,
    edgeStack.HelmConfig?.ChartVersion,
    setFieldValue,
  ]);

  const selectedVersion = useMemo(
    () =>
      versionOptionsQuery.data?.find(
        (v) => v.value?.version === values.helmChartVersion
      )?.value ??
      versionOptionsQuery.data?.[0]?.value ??
      null,
    [versionOptionsQuery.data, values.helmChartVersion]
  );

  const isLatestVersionFetched =
    !versionOptionsQuery.data?.length ||
    selectedVersion?.version === versionOptionsQuery.data?.[0]?.value?.version;

  const chartValuesRefQuery = useHelmChartValues(
    {
      chart: values.helmChartName,
      version: values.helmChartVersion,
      repo: values.helmRepoUrl,
    },
    isLatestVersionFetched
  );

  return (
    <Form className="form-horizontal">
      <EdgeGroupsSelector
        value={values.edgeGroups}
        onChange={(value) => setFieldValue('edgeGroups', value)}
        error={errors.edgeGroups}
      />

      <FormSection title="Deploy to">
        <FormControl
          label="Release name"
          tooltip="Automatically generated based on edge stack name (edge-<name>)"
        >
          <Input
            value={values.helmReleaseName}
            onChange={() => {}}
            readOnly
            disabled
            data-cy="helm-release-name-input"
          />
        </FormControl>

        <FormControl
          label="Namespace"
          errors={errors.helmNamespace}
          required
          tooltip="Kubernetes namespace to deploy the Helm chart into"
        >
          <Input
            value={values.helmNamespace}
            onChange={(e) => setFieldValue('helmNamespace', e.target.value)}
            placeholder="e.g. default"
            data-cy="helm-namespace-input"
          />
        </FormControl>
      </FormSection>

      <FormSection title="Helm chart">
        <FormControl
          label="Helm chart source"
          inputId="helm-chart-source"
          required
          tooltip="Select the Helm repository to browse charts from"
          errors={errors.helmRepoUrl}
        >
          <PortainerSelect<string>
            inputId="helm-chart-source"
            placeholder="Select a repository"
            value={selectedRepoUrl ?? ''}
            options={repoOptionsQuery.data ?? []}
            isLoading={repoOptionsQuery.isInitialLoading}
            onChange={handleRegistryChange}
            isClearable
            data-cy="helm-chart-source-select"
            noOptionsMessage={() =>
              'No repositories found. Add one in User Settings → Helm repositories.'
            }
          />
        </FormControl>

        <HelmTemplatesSelect
          selectedChart={selectedChart}
          onSelect={handleChartSelect}
          isLoadingCharts={chartListQuery.isInitialLoading}
          charts={chartListQuery.data ?? []}
          selectedRegistry={selectedRegistryValue}
          isRegistryError={chartListQuery.isError}
          isOCI={false}
          formError={errors.helmChartName}
        />
      </FormSection>

      <FormSection title="Upgrade options" className="mt-4">
        <FormControl label="Version" inputId="helm-version-input">
          <HelmVersionSelect
            value={selectedVersion}
            options={versionOptionsQuery.data ?? []}
            onChange={(v) =>
              setFieldValue('helmChartVersion', v?.version ?? '')
            }
            data-cy="helm-version-input"
            isLoading={versionOptionsQuery.isInitialLoading}
            isChartSelected={!!values.helmChartName}
          />
        </FormControl>
        <HelmValuesInput
          values={values.helmValuesInline}
          setValues={(v) => setFieldValue('helmValuesInline', v)}
          valuesRef={chartValuesRefQuery.data?.values ?? ''}
          isValuesRefLoading={chartValuesRefQuery.isInitialLoading}
        />
      </FormSection>

      <HelmInstallOptionsSection />

      <StaggerFieldset
        values={values.staggerConfig}
        onChange={(newStaggerValues) =>
          setValues((prev) => ({
            ...prev,
            staggerConfig: { ...prev.staggerConfig, ...newStaggerValues },
          }))
        }
        errors={errors.staggerConfig}
      />

      <FormSection title="Actions">
        <div className="form-group">
          <div className="col-sm-12">
            <LoadingButton
              className="!ml-0"
              data-cy="update-stack-button"
              size="small"
              disabled={!isValid || staggerUpdating}
              isLoading={isLoading}
              loadingText="Update in progress..."
            >
              Update the stack
            </LoadingButton>
          </div>
        </div>
      </FormSection>
    </Form>
  );

  function handleRegistryChange(repoUrl: string | null) {
    setSelectedRepoUrl(repoUrl);
    setSelectedChart(null);
    setFieldValue('helmRepoUrl', repoUrl ?? '');
    setFieldValue('helmChartName', '');
    setFieldValue('helmChartVersion', '');
    setFieldValue('helmValuesInline', '');
  }

  function handleChartSelect(chart: Chart | null) {
    setSelectedChart(chart);
    setFieldValue('helmChartName', chart?.name ?? '');
    setFieldValue('helmRepoUrl', chart?.repo ?? selectedRepoUrl ?? '');
    setFieldValue('helmChartVersion', chart?.versions[0] ?? '');
    if (!chart) {
      setFieldValue('helmValuesInline', '');
    }
    // Reset auto-select so new versions are pre-selected
    didAutoSelectRef.current = false;
  }
}

function formValidation(): SchemaOf<FormValues> {
  return object({
    edgeGroups: array()
      .of(number().required())
      .required()
      .min(1, 'At least one edge group is required'),
    helmRepoUrl: string()
      .url('Helm chart source must be a valid URL')
      .required('Helm chart source is required'),
    helmChartName: string().required('Helm chart is required'),
    helmChartVersion: string().default(''),
    helmValuesInline: string().default(''),
    helmAtomic: boolean().default(false),
    helmTimeout: string().default(''),
    helmNamespace: string()
      .required('Namespace is required')
      .default('default'),
    helmReleaseName: string().default(''),
    staggerConfig: staggerConfigValidation(),
  });
}
